import type {
  PlaceCoords,
  RuteroCircuitAgeBand,
  RuteroConnectorOccupancyLimits,
  RuteroVertical,
} from '@bandido/shared/types';
import { RUTERO_HOLD_PASSENGER_MAX } from '@bandido/shared/types';

import { routeMapEndpoints } from './routeMap';
import type { RouteMapPoint } from './routeMap';
import type {
  BusesSearchPayload,
  CarsSearchPayload,
  CircuitsSearchPayload,
  EsimsSearchPayload,
  FerrySearchPayload,
  PassesSearchPayload,
  RailSearchPayload,
  RuteroPassengerPayload,
  ToursSearchPayload,
  TransferPointPayload,
  TransfersSearchPayload,
} from './queries';

/** Passenger quantity attached to the committed rail result generation. */
export function railBookingPassengerCount(payload: RailSearchPayload | null): number {
  return Array.isArray(payload?.passengers) ? payload.passengers.length : (payload?.passengers ?? 1);
}

/**
 * Ferry ages are the most specific committed passenger representation. Their
 * length wins when present; passenger typing remains unchanged until the
 * provider's age/type contract is verified separately.
 */
export function ferryBookingPassengerCount(payload: FerrySearchPayload | null): number {
  return payload?.passengerAges?.length ?? payload?.passengers ?? 1;
}

/** Passenger quantity attached to the committed adult-only tour search. */
export function tourBookingPassengerCount(payload: ToursSearchPayload | null): number {
  return payload?.partySize ?? 0;
}

/**
 * Un pasajero TAL COMO LO COMITEÓ la búsqueda que produjo esta oferta.
 *
 * Es lo que sabe el PEDIDO, no lo que el asesor va a tipear: de acá salen las
 * filas del formulario y la frase que declara la composición arriba del bloque.
 * Una sola derivación para las dos cosas — con dos, la frase puede decir algo
 * distinto de las filas que dibuja.
 */
export interface CommittedPassenger {
  /**
   * Lo ÚNICO que el contrato de hold transporta (`RuteroPassenger.type`).
   * Solo se deriva de la edad cuando el contrato publica el corte aplicable
   * (`childMaxAge` o `ageBands`). Sin esa autoridad el flujo falla cerrado.
   */
  type: RuteroPassengerPayload['type'];
  /**
   * `false` cuando el pedido conoce solamente una cantidad. El `type` sigue
   * siendo el valor técnico mínimo del payload de hold, pero la UI no puede
   * presentarlo como una clasificación que nadie eligió ni publicó.
   */
  classificationKnown?: false;
  /**
   * La edad que comiteó la búsqueda, cuando la búsqueda la pide. Rotula la
   * fila para que el asesor vea a quién cubre la reserva; NO viaja al hub,
   * porque el contrato de pasajero no tiene dónde ponerla.
   */
  age?: number;
  /** El hold debe traer birthDate para demostrar la edad cotizada. */
  birthDateRequired?: true;
  /** Ya elegida en la búsqueda (pases: decide la familia del producto). */
  nationality?: string;
  /** Circuitos: habitación elegida en la ocupación, base cero. */
  roomIndex?: number;
}

export interface CommittedPartySnapshot {
  quoteAuthority: string | null;
  party: CommittedPassenger[];
}

/** La composición queda ligada a la provenance; cambios del buscador no la mueven. */
export function committedPartySnapshot(
  current: CommittedPartySnapshot | null,
  quoteAuthority: string | null,
  party: readonly CommittedPassenger[] | undefined,
): CommittedPartySnapshot {
  if (current?.quoteAuthority === quoteAuthority) return current;
  return {
    quoteAuthority,
    party: (party ?? []).map((passenger) => ({ ...passenger })),
  };
}

/**
 * Con qué pedido se arma la composición de cada vertical.
 *
 * `CommittedPartyInput` lo INDEXA con cada `RuteroVertical`, así que agregar un
 * vertical al contrato sin decidir de dónde sale su composición no compila
 * (21.22.4). Ojo con la forma que parece equivalente y no lo es: un
 * `interface ... extends Record<RuteroVertical, unknown>` hereda las claves
 * como `unknown` en vez de exigirlas, y el vertical faltante pasa en silencio.
 */
type CommittedPartyPayloads = {
  transfers: TransfersSearchPayload;
  ferry: FerrySearchPayload;
  rail: RailSearchPayload;
  buses: BusesSearchPayload;
  circuits: CircuitsSearchPayload;
  tours: ToursSearchPayload;
  passes: PassesSearchPayload;
  esims: EsimsSearchPayload;
  cars: CarsSearchPayload;
};

type NonCircuitVertical = Exclude<RuteroVertical, 'circuits'>;

export type CommittedPartyInput =
  | {
      [V in NonCircuitVertical]: {
        vertical: V;
        payload: CommittedPartyPayloads[V] | null;
      };
    }[NonCircuitVertical]
  | {
      vertical: 'circuits';
      payload: CircuitsSearchPayload | null;
      occupancyLimits?: RuteroConnectorOccupancyLimits;
      ageBands?: RuteroCircuitAgeBand[];
    };

/**
 * La categoría de tarifa de una edad en ferry, con los cortes que PUBLICA el
 * contrato: infante 0-3, menor 4-15, adulto 16-59, senior 60+.
 *
 * No es una regla nuestra ni una adivinanza: sale del contrato, igual que el
 * resto del vocabulario.
 *
 * Y es la ÚNICA señal que le llega al proveedor: el pasajero del hold no
 * tiene campo `age` —sus campos son `type`, `birthDate` y los datos
 * personales—, así que la edad comiteada se usa para elegir esta categoría y
 * para rotular la fila, y nada más. De una edad no se deriva un `birthDate`:
 * sería inventarle una fecha de nacimiento a alguien. Por eso el corte tiene
 * que estar bien acá: un pasajero de 60 mandado como adulto es la tarifa de
 * senior que no se le cobró.
 */
function ferryFareType(age: number): CommittedPassenger['type'] {
  if (age <= 3) return 'infant';
  if (age <= 15) return 'child';
  if (age <= 59) return 'adult';
  return 'senior';
}

/** N adultos: lo único que sabe una búsqueda que solo comitea una cantidad. */
function adults(count: number): CommittedPassenger[] {
  return Array.from({ length: Math.max(1, count) }, () => ({
    type: 'adult' as const,
  }));
}

/**
 * La cantidad elegida explícitamente dentro del detalle de Tours. Cero sigue
 * significando composición desconocida; nunca se transforma en un ocupante.
 */
export function tourPartyFromCount(count: number): CommittedPassenger[] {
  if (!Number.isInteger(count) || count < 1) return [];
  return adults(Math.min(count, RUTERO_HOLD_PASSENGER_MAX)).map((passenger) => ({
    ...passenger,
    classificationKnown: false as const,
  }));
}

/** Conserva el orden del grupo y adjunta solo las edades aceptadas por quote. */
export function tourPartyWithQuoteAges(
  party: readonly CommittedPassenger[],
  ages: readonly number[] | undefined,
): CommittedPassenger[] {
  if (!ages || ages.length !== party.length) {
    return party.map((passenger) => ({ ...passenger }));
  }
  return party.map((passenger, index) => ({
    ...passenger,
    age: ages[index],
    classificationKnown: false as const,
  }));
}

function circuitPassengerType(
  age: number,
  limits: RuteroConnectorOccupancyLimits | undefined,
  ageBands: readonly RuteroCircuitAgeBand[] | undefined,
): CommittedPassenger['type'] | undefined {
  if (limits?.childMaxAge !== undefined) {
    return age <= limits.childMaxAge ? 'child' : 'adult';
  }
  const matches = ageBands?.filter(
    (band) => age >= band.minAge && age <= band.maxAge,
  );
  return matches?.length === 1 ? matches[0].kind : undefined;
}

/**
 * La composición del pedido comiteado, para los nueve verticales.
 *
 * Devuelve EXACTAMENTE lo que esa búsqueda pidió, ni más ni menos: donde el
 * contrato fija adultos se conserva ese default; Tours count-only marca la
 * clasificación como desconocida; donde se comitean edades o tipos, esos.
 * Ningún vertical presenta una composición que su búsqueda no tiene.
 */
export function committedParty(input: CommittedPartyInput): CommittedPassenger[] {
  switch (input.vertical) {
  case 'transfers':
    // El tipo del ocupante ya es del pedido, y la edad del MENOR la tipea el
    // asesor. La del adulto no: el buscador la fija en 30 para armar el
    // pedido, así que mostrarla afirmaría "30 años" sobre un dato que nadie
    // cargó. A la pantalla solo llega lo que alguien decidió.
    return (input.payload?.occupants ?? [{ type: 'ADL' }]).map((occupant) => ({
      type: occupant.type === 'ADL' ? ('adult' as const) : ('youth' as const),
      ...(occupant.type !== 'ADL' && typeof occupant.age === 'number'
        ? { age: occupant.age }
        : {}),
    }));

  case 'ferry': {
    // Las edades son REALES y son del pedido, y el contrato publica los
    // cortes con los que se convierten en la categoría de tarifa que el
    // proveedor cobra. Un nene de 7 que viaja como adulto es la tarifa
    // equivocada cobrada sobre una reserva real.
    const ages = input.payload?.passengerAges;
    if (ages?.length) {
      return ages.map((age) => ({ type: ferryFareType(age), age }));
    }
    return adults(ferryBookingPassengerCount(input.payload));
  }

  case 'passes':
    // El único vertical que ya comiteaba tipo y nacionalidad por pasajero.
    return (input.payload?.passengers ?? []).length
      ? (input.payload?.passengers ?? []).map((pax) => ({
        type: pax.type,
        age: pax.age,
        nationality: pax.nationality,
      }))
      : adults(1);

  case 'rail':
    return Array.isArray(input.payload?.passengers)
      ? input.payload.passengers.map(({ type, age }) => ({
        type,
        ...(age === undefined ? {} : { age }),
        ...(type === 'youth' || type === 'senior'
          ? { birthDateRequired: true as const }
          : {}),
      }))
      : adults(railBookingPassengerCount(input.payload));

  case 'tours':
    return input.payload?.partySize
      ? tourPartyFromCount(tourBookingPassengerCount(input.payload))
      : [];

  case 'buses':
    return adults(input.payload?.passengers ?? 1);

  case 'circuits':
    if (input.payload?.occupancy?.rooms.length) {
      const party = input.payload.occupancy.rooms.flatMap((room, roomIndex) =>
        room.ages.map((age) => ({
          type: circuitPassengerType(
            age,
            input.occupancyLimits,
            input.ageBands,
          ),
          age,
          roomIndex,
        })),);
      if (party.some((passenger) => passenger.type === undefined)) return [];
      return party as CommittedPassenger[];
    }
    return adults(input.payload?.pax ?? 1);

    // Una eSIM es de una persona y un auto lo maneja un conductor: la
    // búsqueda no comitea ninguna composición porque no hay ninguna.
  case 'esims':
  case 'cars':
    return adults(1);
  }
}

/**
 * La composición agrupada para decirla en palabras, en el orden en que aparece
 * en las filas. Sale de la MISMA lista que las dibuja (21.9): la frase no
 * puede describir un grupo distinto del que se ve abajo.
 */
export interface CommittedPartyGroup {
  type: RuteroPassengerPayload['type'] | 'passenger';
  count: number;
  /** Las edades del grupo, si la búsqueda las comiteó. */
  ages: number[];
}

export function committedPartyGroups(
  party: readonly CommittedPassenger[],
): CommittedPartyGroup[] {
  const groups: CommittedPartyGroup[] = [];
  for (const pax of party) {
    const type = pax.classificationKnown === false ? 'passenger' : pax.type;
    const group = groups.find((entry) => entry.type === type);
    if (group) {
      group.count += 1;
      if (typeof pax.age === 'number') group.ages.push(pax.age);
      continue;
    }
    groups.push({
      type,
      count: 1,
      ages: typeof pax.age === 'number' ? [pax.age] : [],
    });
  }
  return groups;
}

export interface TransferCommittedBookingContext {
  luggage?: TransfersSearchPayload['luggage'];
  tripType: TransfersSearchPayload['tripType'];
  origin: TransferPointPayload;
  destination: TransferPointPayload;
  date: string;
  endDate?: string;
  pickupTime: string;
  returnPickupTime?: string;
  passengerTypes: ('adult' | 'youth')[];
}

/** Route, schedule, trip type and occupants from the committed transfer key. */
export function transferCommittedBookingContext(
  payload: TransfersSearchPayload | null,
): TransferCommittedBookingContext | null {
  if (!payload) return null;

  return {
    tripType: payload.tripType,
    ...(payload.luggage ? { luggage: { ...payload.luggage } } : {}),
    origin: payload.origin,
    destination: payload.destination,
    date: payload.date,
    ...(payload.endDate ? { endDate: payload.endDate } : {}),
    pickupTime: payload.originTime,
    ...(payload.destinationTime ? { returnPickupTime: payload.destinationTime } : {}),
    passengerTypes: payload.occupants.map((occupant) => (occupant.type === 'ADL' ? 'adult' : 'youth')),
  };
}

/**
 * Coordenadas de un punto del PEDIDO, o `undefined` si no se conocen.
 *
 * Dos formas, mutuamente excluyentes por el `kind` del punto comiteado:
 *
 * - `detail` con `lat`/`lng`: la dirección exacta que el asesor eligió o
 *   geocodificó; las coordenadas SON el pedido (un IATA tipeado a mano es
 *   `detail` sin geo y no aporta ninguna).
 * - `place`: el pedido es el `plc_*` y nada más — Bandido nunca lo traduce a
 *   coordenadas para buscar. Para el mapa se resuelve aparte, contra el
 *   maestro (`placeCoords.ts`), y llega acá indexado POR ESE id: no hay forma
 *   de que un punto reciba la coordenada de otro.
 */
function transferPointCoords(
  point: TransferPointPayload,
  placeCoords: Record<string, PlaceCoords>,
): PlaceCoords | undefined {
  if (point.kind === 'place') {
    return point.place ? placeCoords[point.place] : undefined;
  }
  if (typeof point.lat !== 'number' || typeof point.lng !== 'number') {
    return undefined;
  }
  return { lat: point.lat, lng: point.lng };
}

/**
 * La dirección textual del punto COMITEADO, para nombrar su pin en el mapa.
 *
 * Solo la trae el punto `detail` con dirección —la que el asesor eligió en el
 * buscador de direcciones—: un `place` es el `plc_*` y nada más, y un IATA
 * tipeado a mano no tiene dirección. Sin ella el mapa nombra el extremo por
 * sus coordenadas, como hasta ahora.
 */
function transferPointAddress(point: TransferPointPayload): string | undefined {
  return point.kind === 'detail' ? point.address : undefined;
}

/**
 * Origen y destino del transfer COMMITEADO para el mapa del recorrido (#2228,
 * ampliado por #2247 a los puntos elegidos del maestro de lugares).
 *
 * Sale del pedido, no del formulario vivo: si el asesor cambió el origen
 * después de buscar, el mapa sigue siendo el de la búsqueda que produjo la
 * oferta que está por reservar.
 *
 * Vacío cuando alguno de los dos puntos no tiene coordenadas — sin los dos no
 * hay recorrido que dibujar, y el que falta nunca se completa con otra cosa.
 */
export function transferRouteMapPoints(
  context: TransferCommittedBookingContext | null,
  labels: { origin?: string; destination?: string } = {},
  placeCoords: Record<string, PlaceCoords> = {},
): RouteMapPoint[] {
  if (!context) return [];
  return routeMapEndpoints(
    {
      label: labels.origin,
      address: transferPointAddress(context.origin),
      coords: transferPointCoords(context.origin, placeCoords),
    },
    {
      label: labels.destination,
      address: transferPointAddress(context.destination),
      coords: transferPointCoords(context.destination, placeCoords),
    },
  );
}
